import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Conservation3D } from './Conservation3D';
import {
  DEFAULT_EXPLORE_REACTION_ID,
  EXPLORE_REACTIONS,
  MASS_CHALLENGES,
  MASS_QUESTION,
  calculateConservationExplore,
  conservationFields,
  defaultConservationSetup,
  findExploreReaction,
  type ConservationSetup,
  type ExploreReaction,
} from './conservationExplore';
import { formatGrams, type SystemType } from './conservationOfMass';
import { BalanceScene } from './ConservationOfMassVisuals';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

function equationText(equation: string) {
  return equation
    .replace('->', '→')
    .replace(/([A-Za-z)])(\d+)/g, (_, letter: string, digits: string) =>
      `${letter}${digits.replace(/\d/g, (digit) => '₀₁₂₃₄₅₆₇₈₉'[Number(digit)])}`,
    );
}

const SYSTEM_LABEL: Record<SystemType, string> = { open: 'Open', closed: 'Closed (sealed)' };

/**
 * Conservation Explore: the learner sets the limiting mass, open or closed and the reaction
 * progress; the worked steps, the balance reading and the flask all come from one result. A setup
 * the model does not cover shows a specific message and keeps the last valid view.
 */
export function ConservationExplore() {
  const firstReaction = findExploreReaction(DEFAULT_EXPLORE_REACTION_ID) as ExploreReaction;
  const [reactionId, setReactionId] = useState(firstReaction.id);
  const [system, setSystem] = useState<SystemType>('open');
  const [setup, setSetup] = useState<ConservationSetup>(defaultConservationSetup(firstReaction));
  const [message, setMessage] = useState<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;
  const result = useMemo(() => calculateConservationExplore(reactionId, system, setup), [reactionId, system, setup]);
  const { reaction, entry, model } = result;

  const chooseSystem = (next: SystemType) => {
    try {
      calculateConservationExplore(reactionId, next, setup);
      setSystem(next);
      setMessage(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'This setup is outside the model.');
    }
  };

  const chooseReaction = (next: ExploreReaction) => {
    const nextSetup = { ...defaultConservationSetup(next), progressPercent: setup.progressPercent };
    const nextSystem: SystemType = system === 'closed' && !next.closedCaseId ? 'open' : system;
    setReactionId(next.id);
    setSetup(nextSetup);
    setSystem(nextSystem);
    setMessage(nextSystem !== system ? `${next.closedReason ?? ''} The view switched to an open system.`.trim() : null);
  };

  const readingLabel = `${system === 'closed' ? 'Sealed' : 'Open'} flask on a balance reading ${formatGrams(result.readingG)}`;

  return (
    <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6" aria-labelledby="mass-explore-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="mass-explore-heading" className="text-xl font-extrabold text-ink">
            Weigh your own reaction
          </h3>
          <p className="mt-1 max-w-2xl text-sm font-medium leading-6 text-slate2">
            Choose the reaction and the mass of the reactant that runs out, seal it or leave it open, then move the reaction along.
          </p>
        </div>
        {reaction.scene === 'flask' ? (
          <button
            type="button"
            onClick={() => setPlaying(!moving)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
          >
            {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {moving ? 'Pause bubbles' : 'Play bubbles'}
          </button>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Explore reaction">
        {EXPLORE_REACTIONS.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === reactionId}
            onClick={() => chooseReaction(candidate)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === reactionId ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2'
            }`}
          >
            {candidate.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="grid grid-cols-2 rounded-full bg-surface p-1" role="group" aria-label="System">
          {(['open', 'closed'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={system === option}
              onClick={() => chooseSystem(option)}
              className={`rounded-full px-4 py-1.5 text-sm font-extrabold ${system === option ? 'bg-ink text-white' : 'text-slate2'}`}
            >
              {SYSTEM_LABEL[option]}
            </button>
          ))}
        </div>
        <p className="break-words font-mono text-sm font-bold text-ink">{equationText(entry.equation)}</p>
      </div>
      {message ? (
        <p role="alert" className="mt-3 rounded-2xl bg-wash-coral px-4 py-2 text-sm font-bold text-ink">
          {message}
        </p>
      ) : null}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <figure className="min-w-0">
          <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
          {reaction.scene === 'flask' ? (
            <Conservation3D
              state={result.scene}
              playing={moving}
              label={`${readingLabel}${result.scene.closure === 'balloon' ? `, balloon holding ${result.gasVolumeL.toFixed(2)} L of gas` : ''}`}
            />
          ) : (
            <div className="rounded-2xl border border-hairline bg-surface p-4">
              <BalanceScene entry={entry} model={model} revealed={result.shownProgress > 0} readingG={result.readingG} />
            </div>
          )}
          <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-ink px-4 py-3 text-white">
            <span className="text-xs font-extrabold uppercase tracking-wide">Balance reading</span>
            <span className="font-mono text-2xl font-extrabold" data-testid="balance-reading">
              {formatGrams(result.readingG)}
            </span>
          </div>
          <p className="mt-2 text-xs font-bold leading-5 text-slate2">
            Before: {formatGrams(model.beforeG)} · all reacted: {formatGrams(model.afterG)} · {entry.boundary}
          </p>
        </figure>
        <div className="min-w-0 space-y-4">
          <ModelInputPanel
            key={reactionId}
            legend="Set up the reaction"
            fields={conservationFields(reaction)}
            values={setup}
            onChange={(next) => setSetup(next as ConservationSetup)}
            sliderKey="progressPercent"
          />
          <ExploreChallenges question={MASS_QUESTION} challenges={MASS_CHALLENGES} result={result} />
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="mass-step"
            result={result.result}
            resultTestId="mass-result"
            limitNote={result.limitNote}
          />
        </div>
      </div>
    </section>
  );
}
